import React, { useState, useMemo } from 'react'
import { useSiteData } from '../context/SiteDataContext.jsx'
import { buildResponsiveImageProps, excerptFromHtml, getCmsPageText } from '../lib/cms.js'
import { Container, Card, SectionHeading, ButtonLink } from '../components/atoms.jsx'
import { PageHero, RichContent, SeoManager } from '../components/content.jsx'
import { ArrowRight, Search, Phone, Mail, MapPin, CheckCircle } from 'lucide-react'

// Categorization helper for filter pills
const CATEGORY_KEYWORDS = {
  'ryg-smerter': ['ryg', 'diskusprolaps', 'iskias', 'lænd', 'smerter', 'håndled', 'ben', '23 års smertehelved'],
  'kol-lunger': ['kol', 'lunge', 'hoste', 'astma', 'vejrtrækning'],
  'stress-angst': ['stress', 'angst', 'mareridt', 'søvnløshed', 'selvtillid', 'dødsangst', 'panikangst', 'tankefeltterapi', 'tapping'],
  'skulder-nakke': ['skulder', 'nakke', 'frossen skulder', 'piskesmæld', 'lårben'],
  'sport': ['sport', 'idræt', 'fiber', 'crosser', 'bowling', 'knæ', 'lyske', 'tennisalbue', 'løb'],
  'oere-tinnitus': ['øre', 'tinnitus', 'øresusen', 'menieres', 'balancenerve', 'mellemørebetændelse', 'tårekanal'],
  'fertilitet': ['barnløs', 'fertilitet', 'ønskebarn', 'gravid', 'bækkenløshed', 'kvalme'],
  'hovedpine': ['hovedpine', 'migræne', 'horton']
}

export function PatientStoriesPage() {
  const { getPage, patienthistorier } = useSiteData()
  const page = getPage('patienthistorier')
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('alle')

  const categories = [
    { id: 'alle', label: 'Alle historier' },
    { id: 'ryg-smerter', label: 'Rygsmerter & Gigt' },
    { id: 'kol-lunger', label: 'KOL & Luftveje' },
    { id: 'stress-angst', label: 'Stress & Angst' },
    { id: 'skulder-nakke', label: 'Skulder & Nakke' },
    { id: 'sport', label: 'Sportsskader' },
    { id: 'oere-tinnitus', label: 'Øre & Tinnitus' },
    { id: 'fertilitet', label: 'Fertilitet & Graviditet' },
    { id: 'hovedpine', label: 'Hovedpine & Migræne' },
  ]

  const filteredStories = useMemo(() => {
    return patienthistorier.filter((story) => {
      const title = (story?.title || '').toLowerCase()
      const summary = (story?.summary || '').toLowerCase()
      const content = (story?.content || '').toLowerCase()
      const query = searchQuery.trim().toLowerCase()

      const matchesSearch =
        !query ||
        title.includes(query) ||
        summary.includes(query) ||
        content.includes(query)

      if (!matchesSearch) return false

      if (selectedCategory === 'alle') return true

      const keywords = CATEGORY_KEYWORDS[selectedCategory] || []
      const textToMatch = `${title} ${summary} ${story?.terms?.treatment?.join(' ') || ''}`.toLowerCase()
      return keywords.some((kw) => textToMatch.includes(kw) || content.includes(kw))
    })
  }, [patienthistorier, searchQuery, selectedCategory])

  return (
    <>
      <SeoManager
        title={page?.meta_title || 'Patienthistorier | Helseklinikken'}
        description={page?.meta_description || 'Siden 1981 har masser af mennesker besøgt Helseklinikken i Gjerrild.'}
        image={page?.image?.[0] || 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg'}
        canonical="https://helselarsen.dk/patienthistorier/"
      />

      <main>
        {/* Top Hero Section */}
        <section 
          className="border border-forest-900 bg-forest-950 py-20 sm:py-28 lg:py-36 min-h-[380px] sm:min-h-[460px] lg:min-h-[540px] flex items-center"
          style={{ 
            backgroundImage: `linear-gradient(180deg, rgba(16, 32, 22, 0.15) 0%, rgba(16, 32, 22, 0.15) 100%), url("${(page?.image?.[0] || 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg').replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Container>
            <PageHero
              page={page}
              kicker="Patienthistorier"
              description="Siden 1981 har masser af mennesker besøgt Helseklinikken i Gjerrild. Læs beretningerne fra vores patienter om deres erfaringer og resultater."
              actions={[
                { label: 'Kontakt os', href: '/kontakt' },
                { label: 'Se galleri', href: '/galleri', variant: 'secondary' },
              ]}
            />
          </Container>
        </section>

        {/* Content & Stories Section */}
        <section className="py-12 lg:py-16 bg-white">
          <Container className="space-y-10">
            {/* Intro Text Box from helselarsen.dk */}
            <div className="bg-stone-50 border border-stone-200 p-6 sm:p-8 rounded-xl shadow-xs space-y-4">
              <h1 className="font-serif text-2xl sm:text-3xl font-bold text-forest-950">
                Patienthistorier
              </h1>
              <p className="font-semibold text-forest-900 text-lg">
                Siden 1981 har masser af mennesker besøgt Helseklinikken i Gjerrild
              </p>
              <p className="text-base text-stone-700 leading-relaxed">
                Glade patienter har valgt at fortælle/skrive deres ”historie” til{' '}
                <a href="https://www.helselarsen.dk" className="text-forest-800 font-semibold underline hover:text-forest-950">
                  www.helselarsen.dk
                </a>{' '}
                for at støtte den alternative behandling og for at give dig og andre mulighed for at høre om deres erfaringer. Alle har givet deres fuldmagt til at ”helselarsen” benytter deres historie.
              </p>
              <p className="text-sm text-stone-600">
                Personerne som udtaler sig, og historierne kommer i vilkårlig rækkefølge.
              </p>
              <p className="text-xs italic text-stone-500">
                ”helselarsen” står ikke til ansvar for indholdet af historierne.
              </p>
              <p className="font-semibold text-forest-800 pt-2">
                God fornøjelse med historierne!
              </p>
            </div>

            {/* Filter & Search Bar */}
            <div className="space-y-4 bg-sand-50/70 border border-stone-200 p-5 rounded-xl">
              <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                  <input
                    type="text"
                    placeholder="Søg efter navn, lidelse eller symptom..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-600 focus:border-transparent text-stone-800 placeholder-stone-400"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-stone-400 hover:text-stone-600"
                    >
                      Ryd
                    </button>
                  )}
                </div>
                <div className="text-xs text-stone-500 font-medium">
                  Viser {filteredStories.length} ud af {patienthistorier.length} historier
                </div>
              </div>

              {/* Category Pills */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all ${
                      selectedCategory === cat.id
                        ? 'bg-forest-800 text-white shadow-xs'
                        : 'bg-white text-stone-700 border border-stone-300 hover:bg-stone-100'
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stories Grid */}
            {filteredStories.length === 0 ? (
              <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200 space-y-3">
                <p className="text-stone-600 font-medium">Ingen patienthistorier matchede din søgning.</p>
                <button
                  onClick={() => {
                    setSearchQuery('')
                    setSelectedCategory('alle')
                  }}
                  className="text-xs font-semibold text-forest-800 underline hover:text-forest-950"
                >
                  Nulstil filtre
                </button>
              </div>
            ) : (
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredStories.map((story) => {
                  const hasImage = story.image?.[0]
                  const femaleNames = [
                    'karen', 'dyveke', 'bodil', 'joan', 'pernille', 'charlotte',
                    'zivile', 'doris', 'astrid', 'birthe', 'stine', 'caroline',
                    'heidi', 'aase', 'zunja', 'tanja', 'iben', 'bettina', 'dorte'
                  ]
                  const isFemale = femaleNames.some((name) =>
                    (story.title || '').toLowerCase().includes(name)
                  )
                  const fallbackImage = isFemale
                    ? 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/woman-avatar-copy.jpg'
                    : 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/man-avatar-copy.jpg'
                  const imageUrl = hasImage ? story.image[0] : fallbackImage
                  const slug = story?.slug?.da || story?.slug || ''

                  return (
                    <Card
                      key={story.id}
                      className="flex flex-col gap-4 p-5 h-full bg-white border border-stone-200 rounded-xl hover:shadow-md hover:border-forest-200 transition-all duration-200"
                    >
                      <div className="flex items-start gap-4">
                        <img
                          src={buildResponsiveImageProps(imageUrl, [200, 400]).src}
                          srcSet={buildResponsiveImageProps(imageUrl, [200, 400]).srcSet}
                          sizes="80px"
                          alt={story.title}
                          className="h-20 w-20 shrink-0 rounded-full object-cover border-2 border-forest-100 shadow-xs bg-stone-100"
                          loading="lazy"
                        />
                        <div className="space-y-1 min-w-0 flex-1">
                          <h3 className="text-lg font-serif font-bold text-forest-950 leading-snug">
                            {story.title}
                          </h3>
                          {story.summary && (
                            <p className="text-xs font-semibold text-forest-800 leading-snug">
                              {story.summary}
                            </p>
                          )}
                        </div>
                      </div>

                      <p className="text-xs sm:text-sm text-stone-600 leading-relaxed line-clamp-3">
                        {excerptFromHtml(getCmsPageText(story), 130)}
                      </p>

                      <div className="mt-auto pt-3 border-t border-stone-100 flex justify-between items-center">
                        <ButtonLink
                          href={`/patienthistorie/${slug}`}
                          variant="ghost"
                          className="px-0 text-xs sm:text-sm font-semibold text-forest-800 hover:text-forest-950 flex items-center gap-1"
                        >
                          Læs hele historien <ArrowRight className="h-3.5 w-3.5" />
                        </ButtonLink>
                      </div>
                    </Card>
                  )
                })}
              </div>
            )}
          </Container>
        </section>

        {/* 4. Classic "Book en tid" Section matching helselarsen.dk */}
        <section 
          className="bg-[#a3b899] py-12 sm:py-16 px-4"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(163, 184, 153, 0.75) 0%, rgba(163, 184, 153, 0.75) 100%), url("${(page?.image?.[0] || 'https://assets.auxodev.dk/assets/05dcab11-7a44-4f88-851f-c7c0e22dabc3/patienthistorier.jpg').replace('assets.auxodev.dk/assets/', 'assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/')}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        >
          <Container>
            <div className="max-w-2xl mx-auto bg-white/95 backdrop-blur-xs rounded-2xl p-8 sm:p-12 text-center shadow-xl shadow-stone-900/10 border border-stone-200/90 relative">
              {/* Decorative Clinic Icon Badge */}
              <div className="w-14 h-14 rounded-full bg-forest-50 text-forest-800 flex items-center justify-center mx-auto mb-4 border border-forest-200/70 text-xl shadow-xs">
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              </div>

              <h2 className="text-2xl sm:text-3xl font-bold text-stone-900 mb-3 tracking-tight">
                Book en tid
              </h2>
              
              <p className="text-stone-600 text-sm sm:text-base mb-6 leading-relaxed max-w-lg mx-auto">
                Slip dit ubehag, rygsmerter, idrætsskader, hovedpine, stress og meget mere.
              </p>

              {/* Telefontid Callout Badge */}
              <div className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-stone-50 border border-stone-200/80 rounded-xl text-xs sm:text-sm font-semibold text-stone-800 mb-8 shadow-2xs max-w-full">
                <i className="fa-regular fa-clock text-forest-700 text-sm" aria-hidden="true" />
                <span>Telefon hverdag bedst mellem kl. 8.30 – 9.30</span>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 pt-2 border-t border-stone-100">
                <a
                  href="/kontakt"
                  className="inline-flex items-center justify-center gap-2 bg-[#9e3d34] hover:bg-[#85332b] text-white text-sm font-semibold px-7 py-3 rounded-lg shadow-sm hover:shadow transition-all w-full sm:w-auto"
                >
                  <i className="fa-regular fa-envelope" aria-hidden="true" />
                  <span>Skriv til os her</span>
                </a>
                <a
                  href="tel:86384117"
                  className="inline-flex items-center justify-center gap-2 bg-stone-100/90 hover:bg-stone-200/80 text-stone-900 text-sm sm:text-base font-bold px-6 py-2.5 rounded-lg border border-stone-200 transition-colors w-full sm:w-auto"
                >
                  <i className="fa-solid fa-phone text-forest-700" aria-hidden="true" />
                  <span>(+45) 86 38 41 17</span>
                </a>
              </div>
            </div>
          </Container>
        </section>
      </main>
    </>
  )
}
